import React from "react";
import ReactDOM from "react-dom/client";
import "./index.scss";
import reportWebVitals from "./reportWebVitals";
import { RouterProvider, createBrowserRouter, useSearchParams } from "react-router-dom";
import { ValidateLink } from "./components/ValidateLink";
import { NameAndEmail } from "./components/playerEntryDetails";
import { PlayerPhoto } from "./components/playerEntryPhoto";
import { PlayerName } from "./components/playerEntryName";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import { PlayerEntryPhotoConfirm } from "./components/playerEntryPhotoConfirm";
import { PlayerEntryComplete } from "./components/playerEntryComplete";
import { InvalidLink } from "./components/InvalidLink";
import { CameraError } from "./components/cameraError";
import { SessionCompleted } from "./components/SessionCompleted";
import { SessionCheckResponse } from "./types/player";
import { getVariant } from "./utils/abBucketing";
import { decodeGuidToString } from "./helpers";
import { SessionCompletedB } from "./components/SessionCompletedB";
import { SessionCompletedA } from "./components/SessionCompletedA";

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

// Get the variant for a/b testing on page reload
const SessionCompletedRouter: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("s");
  const region = process.env.REACT_APP_REGION;

  if (region !== "uk" && region !== "dev") {
    return <SessionCompleted />;
  }

  const variant = sessionId ? getVariant(decodeGuidToString(sessionId)) : "B";
  return variant === "A" ? <SessionCompletedA isTestVariant /> : <SessionCompletedB isTestVariant />;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <ValidateLink />,

    // need to confirm that the session exists by calling the CheckSession endpoint
    // if it does, then we can render the player entry
    // if it doesn't, then we need to render the invalid link page
    loader: async ({ request }) => {
      const url = new URL(request.url);
      const sessionId = url.searchParams.get("s");
      if (sessionId === null) {
        return { status: "Not Found" };
      }
      try {
        // call the CheckSession endpoint
        // if "ClientSessionDate" already exists in the session storage, then we need to pass this to the api as a header
        const region = process.env.REACT_APP_REGION;
        const response: Response = await fetch(`/api/v1/${region}/electricshuffle/sessions/${sessionId}`, {
          method: "GET",
          headers: {
            ClientSessionDate: "",
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const sessionStatus: SessionCheckResponse = await response.json();
          return sessionStatus;
        } else {
          return { status: "Not Found" };
        }
      } catch (error) {
        return { status: "Not Found" };
      }
    },
  },

  {
    path: "/Details",
    element: <NameAndEmail />,
  },
  {
    path: "/Photo",
    element: <PlayerPhoto />,
  },
  {
    path: "/Confirm",
    element: <PlayerEntryPhotoConfirm />,
  },
  {
    path: "/Nickname",
    element: <PlayerName />,
  },
  {
    path: "/Complete",
    element: <PlayerEntryComplete />,
  },
  {
    path: "/InvalidLink",
    element: <InvalidLink />,
  },
  {
    path: "/CameraError",
    element: <CameraError />,
  },
  {
    path: "/SessionCompleted",
    element: <SessionCompletedRouter />,
  },
]);

root.render(
  <Provider store={store}>
    <React.StrictMode>
      <RouterProvider router={router} />
    </React.StrictMode>
  </Provider>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
